import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

// ─── Primitive helpers ────────────────────────────────────────────────────────

const METHOD_STYLES = {
  GET: "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30",
  POST: "bg-blue-500/20 text-blue-400 border border-blue-500/30",
  DELETE: "bg-red-500/20 text-red-400 border border-red-500/30",
  PUT: "bg-orange-500/20 text-orange-400 border border-orange-500/30",
  WS: "bg-amber-500/20 text-amber-400 border border-amber-500/30",
  INFO: "bg-slate-500/20 text-slate-400 border border-slate-500/30",
};

const MethodBadge = ({ method }) => (
  <span
    className={`inline-block px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wide ${METHOD_STYLES[method] ?? METHOD_STYLES.INFO}`}
  >
    {method}
  </span>
);

const IC = ({ children }) => (
  <code className="font-mono text-cyan-400 text-xs bg-slate-900/70 px-1.5 py-0.5 rounded border border-slate-700/40">
    {children}
  </code>
);

const CodeBlock = ({ code }) => (
  <div className="bg-slate-950 border border-slate-800 rounded-lg p-4 overflow-x-auto">
    <pre className="font-mono text-sm text-slate-300 leading-relaxed whitespace-pre">
      {code.trim()}
    </pre>
  </div>
);

const CodeTabs = ({ tabs }) => {
  const [active, setActive] = useState(0);
  return (
    <div>
      <div className="flex gap-2 mb-3">
        {tabs.map((tab, i) => (
          <button
            key={tab.label}
            onClick={() => setActive(i)}
            className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
              active === i
                ? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/30"
                : "bg-slate-800 text-slate-400 border border-slate-700 hover:text-slate-200"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <CodeBlock code={tabs[active].code} />
    </div>
  );
};

const ParamTable = ({ params }) => {
  const { t } = useTranslation();
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-slate-700">
            <th className="text-left py-2 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">
              {t("apiDocs.colParameter")}
            </th>
            <th className="text-left py-2 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">
              {t("apiDocs.colType")}
            </th>
            <th className="text-left py-2 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">
              {t("apiDocs.colRequired")}
            </th>
            <th className="text-left py-2 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">
              {t("apiDocs.colDescription")}
            </th>
          </tr>
        </thead>
        <tbody>
          {params.map((p, i) => (
            <tr key={i} className="border-b border-slate-800/60">
              <td className="py-2 px-3 font-mono text-cyan-400 text-xs align-top">
                {p.name}
              </td>
              <td className="py-2 px-3 text-slate-400 text-xs align-top">
                {p.type}
              </td>
              <td className="py-2 px-3 align-top">
                {p.required ? (
                  <span className="text-red-400 text-xs font-bold uppercase">
                    {t("apiDocs.reqRequired")}
                  </span>
                ) : (
                  <span className="text-slate-500 text-xs">
                    {t("apiDocs.reqOptional")}
                  </span>
                )}
              </td>
              <td className="py-2 px-3 text-slate-300 text-xs leading-relaxed align-top">
                {p.desc}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const FieldTable = ({ fields }) => {
  const { t } = useTranslation();
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-slate-700">
            <th className="text-left py-2 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">
              {t("apiDocs.colField")}
            </th>
            <th className="text-left py-2 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">
              {t("apiDocs.colType")}
            </th>
            <th className="text-left py-2 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">
              {t("apiDocs.colDescription")}
            </th>
          </tr>
        </thead>
        <tbody>
          {fields.map((f, i) => (
            <tr key={i} className="border-b border-slate-800/60">
              <td className="py-2 px-3 font-mono text-cyan-400 text-xs align-top">
                {f.name}
              </td>
              <td className="py-2 px-3 text-slate-400 text-xs align-top">
                {f.type}
              </td>
              <td className="py-2 px-3 text-slate-300 text-xs leading-relaxed align-top">
                {f.desc}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SectionTitle = ({ id, children }) => (
  <h2
    id={id}
    className="text-xl font-semibold text-slate-100 border-b border-slate-700/60 pb-3 mb-6 scroll-mt-6"
  >
    {children}
  </h2>
);

const SubTitle = ({ children }) => (
  <h3 className="text-base font-semibold text-slate-200 mb-3 mt-5">
    {children}
  </h3>
);

const EndpointCard = ({ method, url, title, description, children }) => (
  <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl mb-6 overflow-hidden">
    <div className="p-5 border-b border-slate-700/40">
      <div className="font-semibold text-slate-200 mb-2">{title}</div>
      <div className="flex flex-wrap items-center gap-2">
        <MethodBadge method={method} />
        <IC>{url}</IC>
      </div>
      {description && (
        <p className="mt-2 text-sm text-slate-400 leading-relaxed">
          {description}
        </p>
      )}
    </div>
    {children && <div className="p-5 flex flex-col gap-5">{children}</div>}
  </div>
);

const InfoBox = ({ color = "blue", title, children }) => {
  const colors = {
    blue: "border-blue-500/50 bg-blue-500/5",
    amber: "border-amber-500/50 bg-amber-500/5",
    green: "border-emerald-500/50 bg-emerald-500/5",
    red: "border-red-500/50 bg-red-500/5",
  };
  return (
    <div className={`border-l-4 rounded-r-lg p-4 ${colors[color]}`}>
      {title && (
        <div className="font-semibold text-sm text-slate-200 mb-1">{title}</div>
      )}
      <div className="text-sm text-slate-300 leading-relaxed">{children}</div>
    </div>
  );
};

// ─── Navigation (built inside component to support i18n) ─────────────────────

// ─── Code Examples ────────────────────────────────────────────────────────────

const WS_JS = `const apiToken = 'epv_your_token_here';
const enableDiarization = true;
const voiceSignatures = [   // from /v1/generate_signatures
  { name: 'Alice', embedding: [/* 512 dimensions */] },
  { name: 'Bob',   embedding: [/* 512 dimensions */] },
];

const ws = new WebSocket(
  \`wss://api.mayorsoftware.ch/v1/asr?token=\${encodeURIComponent(apiToken)}&enable_diarization=\${enableDiarization}\`
);
let configReceived = false;

ws.onmessage = async (event) => {
  const data = JSON.parse(event.data);

  if (data.type === 'config' && !configReceived) {
    configReceived = true;
    if (enableDiarization) {
      ws.send(JSON.stringify({ type: 'config', voice_signatures: voiceSignatures }));
    } else {
      startAudioCapture();
    }
    return;
  }

  if (data.type === 'config_ack') {
    startAudioCapture();
    return;
  }

  if (data.type === 'ready_to_stop') {
    ws.close();
    return;
  }

  if (data.status === 'active_transcription') {
    data.lines?.forEach(line => {
      if (line.text?.trim()) console.log(\`Speaker \${line.speaker}: \${line.text}\`);
    });
  }
};

function startAudioCapture() {
  navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
    const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
    recorder.ondataavailable = e => {
      if (e.data.size > 0 && ws.readyState === WebSocket.OPEN) ws.send(e.data);
    };
    recorder.start(250);
  });
}

function stopRecording() {
  if (ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify({ type: 'end_session' }));
  }
}`;

const WS_PYTHON = `import asyncio, websockets, json, pyaudio

async def transcribe_live():
    uri = "wss://api.mayorsoftware.ch/v1/asr?token=epv_your_token_here"
    async with websockets.connect(uri) as ws:
        p = pyaudio.PyAudio()
        stream = p.open(format=pyaudio.paInt16, channels=1, rate=16000, input=True, frames_per_buffer=1024)

        async def send_audio():
            while True:
                await ws.send(stream.read(1024))
                await asyncio.sleep(0.05)

        async def receive():
            async for msg in ws:
                data = json.loads(msg)
                if data.get("status") == "active_transcription":
                    for line in data.get("lines", []):
                        print(f"Speaker {line['speaker']}: {line['text']}")

        await asyncio.gather(send_audio(), receive())

asyncio.run(transcribe_live())`;

const GEN_SIG_JS = `const formData = new FormData();
formData.append('audio_files', johnAudioFile);
formData.append('audio_files', janeAudioFile);
formData.append('speaker_names', JSON.stringify(['John Doe', 'Jane Smith']));

const response = await fetch('https://api.mayorsoftware.ch/v1/generate_signatures', {
  method: 'POST',
  headers: { 'Authorization': \`Bearer \${token}\` },
  body: formData,
});

const { signatures } = await response.json();
// signatures = [{ name: 'John Doe', embedding: [/* 512 floats */] }, ...]`;

const GEN_SIG_PYTHON = `import requests

files = [
    ('audio_files', ('john.wav', open('john.wav', 'rb'), 'audio/wav')),
    ('audio_files', ('jane.wav', open('jane.wav', 'rb'), 'audio/wav')),
]
data = { 'speaker_names': '["John Doe", "Jane Smith"]' }

r = requests.post(
    'https://api.mayorsoftware.ch/v1/generate_signatures',
    headers={'Authorization': 'Bearer epv_your_token_here'},
    files=files, data=data,
)
signatures = r.json()['signatures']`;

const SYNC_JS = `const formData = new FormData();
formData.append('audio', audioFile);
formData.append('diarization', 'true');
formData.append('use_voice_signatures', 'true');
formData.append('voice_signatures', JSON.stringify(signatures));
formData.append('voice_similarity_threshold', '0.18');
formData.append('anonymous_similarity_threshold', '0.55');

const response = await fetch('https://api.mayorsoftware.ch/v1/transcribe/sync', {
  method: 'POST',
  headers: { 'Authorization': 'Bearer epv_your_token_here' },
  body: formData,
});

const result = await response.json();
console.log('Transcription:', result.text);
result.segments.forEach(seg => {
  console.log(\`Speaker \${seg.speaker} (\${seg.start}–\${seg.end}): \${seg.text}\`);
});`;

const SYNC_PYTHON = `import requests

r = requests.post(
    'https://api.mayorsoftware.ch/v1/transcribe/sync',
    headers={'Authorization': 'Bearer epv_your_token_here'},
    files={'audio': open('meeting.wav', 'rb')},
    data={'diarization': 'true'},
)
result = r.json()
print(result['text'])
for seg in result['segments']:
    print(f"Speaker {seg['speaker']} ({seg['start']}–{seg['end']}): {seg['text']}")`;

const SUBMIT_JS = `const formData = new FormData();
formData.append('audio', audioFile);
formData.append('diarization', 'true');
formData.append('use_voice_signatures', 'true');
formData.append('voice_signatures', JSON.stringify(signatures));

const response = await fetch('https://api.mayorsoftware.ch/v1/transcribe/submit', {
  method: 'POST',
  headers: { 'Authorization': 'Bearer epv_your_token_here' },
  body: formData,
});
const { task_id } = await response.json();

// Poll for completion
const poll = setInterval(async () => {
  const s = await fetch(\`https://api.mayorsoftware.ch/v1/transcribe/status/\${task_id}\`, {
    headers: { 'Authorization': 'Bearer epv_your_token_here' },
  }).then(r => r.json());

  if (s.status === 'completed') {
    clearInterval(poll);
    const result = await fetch(\`https://api.mayorsoftware.ch/v1/transcribe/result/\${task_id}\`, {
      headers: { 'Authorization': 'Bearer epv_your_token_here' },
    }).then(r => r.json());
    console.log(result.text);
  }
}, 3000);`;

const SUBMIT_PYTHON = `import requests, time

r = requests.post(
    'https://api.mayorsoftware.ch/v1/transcribe/submit',
    headers={'Authorization': 'Bearer epv_your_token_here'},
    files={'audio': open('large_meeting.mp3', 'rb')},
    data={'diarization': 'true'},
)
task_id = r.json()['task_id']

while True:
    status = requests.get(
        f'https://api.mayorsoftware.ch/v1/transcribe/status/{task_id}',
        headers={'Authorization': 'Bearer epv_your_token_here'},
    ).json()
    if status['status'] == 'completed':
        result = requests.get(
            f'https://api.mayorsoftware.ch/v1/transcribe/result/{task_id}',
            headers={'Authorization': 'Bearer epv_your_token_here'},
        ).json()
        print(result['text'])
        break
    time.sleep(3)`;

const LLM_JS = `const response = await fetch('https://api.mayorsoftware.ch/v1/llm/chat/completions', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer epv_your_token_here',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    messages: [
      { role: 'system', content: 'You are a helpful assistant.' },
      { role: 'user',   content: 'Summarize this meeting transcript...' },
    ],
    model: 'Qwen/Qwen3.8-27B-FP8',
    profile_type: 'standard',
    temperature: 1.0,
    max_tokens: 2048,
    stream: false,
  }),
});
const data = await response.json();
console.log(data.choices[0].message.content);`;

// ─── Main component ───────────────────────────────────────────────────────────

const ApiDocumentation = () => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState("introduction");
  const mainRef = useRef(null);

  const NAV = [
    {
      group: t("apiDocs.groupOverview"),
      items: [
        {
          id: "introduction",
          label: t("apiDocs.navIntroduction"),
          method: "INFO",
        },
        {
          id: "authentication",
          label: t("apiDocs.navAuthentication"),
          method: "INFO",
        },
        { id: "base-urls", label: t("apiDocs.navBaseUrls"), method: "INFO" },
        { id: "pricing", label: t("apiDocs.navPricing"), method: "INFO" },
      ],
    },
    {
      group: t("apiDocs.groupStreaming"),
      items: [
        { id: "websocket", label: t("apiDocs.navWebsocket"), method: "WS" },
        { id: "models", label: t("apiDocs.navModels"), method: "GET" },
        { id: "signatures", label: t("apiDocs.navSignatures"), method: "POST" },
        { id: "refinement", label: t("apiDocs.navRefinement"), method: "INFO" },
      ],
    },
    {
      group: t("apiDocs.groupTranscription"),
      items: [
        {
          id: "transcribe-sync",
          label: t("apiDocs.navTranscribeSync"),
          method: "POST",
        },
        {
          id: "transcribe-submit",
          label: t("apiDocs.navTranscribeSubmit"),
          method: "POST",
        },
        {
          id: "transcribe-status",
          label: t("apiDocs.navTranscribeStatus"),
          method: "GET",
        },
        {
          id: "transcribe-result",
          label: t("apiDocs.navTranscribeResult"),
          method: "GET",
        },
        {
          id: "transcribe-cancel",
          label: t("apiDocs.navTranscribeCancel"),
          method: "DELETE",
        },
        {
          id: "reference-transcript",
          label: t("apiDocs.navReferenceTranscript"),
          method: "GET",
        },
      ],
    },
    {
      group: t("apiDocs.groupMonitoring"),
      items: [
        { id: "stats", label: t("apiDocs.navStats"), method: "GET" },
        { id: "health", label: t("apiDocs.navHealth"), method: "GET" },
      ],
    },
    {
      group: t("apiDocs.groupLlm"),
      items: [
        { id: "llm-chat", label: t("apiDocs.navLlmChat"), method: "POST" },
      ],
    },
  ];

  useEffect(() => {
    const sections = document.querySelectorAll("section[id]");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActiveSection(e.target.id);
        });
      },
      { rootMargin: "-20% 0px -70% 0px" },
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="-mx-4 -my-8 sm:-mx-6 lg:-mx-10 flex min-h-screen">
      {/* Docs sidebar */}
      <nav className="hidden xl:flex w-56 2xl:w-64 shrink-0 flex-col sticky top-0 h-screen overflow-y-auto bg-[#080e1c]/80 border-r border-slate-800/60 py-6">
        <div className="px-4 mb-5">
          <div className="text-xs font-bold uppercase tracking-widest text-cyan-400 mb-1">
            ePV Platform
          </div>
          <div className="text-xs text-slate-500">
            {t("apiDocs.apiReference")}
          </div>
        </div>
        {NAV.map((group) => (
          <div key={group.group} className="px-3 mb-4">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 px-1">
              {group.group}
            </div>
            {group.items.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                className={`flex items-center gap-2 px-2 py-1 rounded text-xs transition-colors ${
                  activeSection === item.id
                    ? "bg-slate-800 text-slate-100"
                    : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50"
                }`}
              >
                <MethodBadge method={item.method} />
                {item.label}
              </a>
            ))}
          </div>
        ))}
      </nav>

      {/* Main content */}
      <div
        ref={mainRef}
        className="flex-1 min-w-0 px-6 py-8 lg:px-10 max-w-5xl"
      >
        {/* Page header */}
        <div className="mb-10">
          <h1 className="text-3xl font-bold text-slate-100 mb-2">
            {t("apiDocs.pageTitle")}
          </h1>
          <p className="text-slate-400 max-w-2xl">
            {t("apiDocs.pageSubtitle")}
          </p>
          <div className="mt-4 px-4 py-3 bg-slate-800/40 border border-slate-700/50 rounded-lg text-sm text-slate-400">
            <span className="font-semibold text-cyan-400">
              {t("apiDocs.updateLabel")}
            </span>{" "}
            {t("apiDocs.updateText")}
          </div>
        </div>

        {/* ── Introduction ── */}
        <section id="introduction">
          <SectionTitle id="introduction">
            {t("apiDocs.introTitle")}
          </SectionTitle>
          <p className="text-slate-400 text-sm leading-relaxed mb-6">
            {t("apiDocs.introBody")}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              {
                title: t("apiDocs.featureAccuracyTitle"),
                desc: t("apiDocs.featureAccuracyDesc"),
              },
              {
                title: t("apiDocs.featureRealtimeTitle"),
                desc: t("apiDocs.featureRealtimeDesc"),
              },
              {
                title: t("apiDocs.featureSecurityTitle"),
                desc: t("apiDocs.featureSecurityDesc"),
              },
            ].map((f) => (
              <div
                key={f.title}
                className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4"
              >
                <div className="font-semibold text-slate-200 mb-1 text-sm">
                  {f.title}
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">
                  {f.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Authentication ── */}
        <section id="authentication" className="mt-10">
          <SectionTitle id="authentication">
            {t("apiDocs.authTitle")}
          </SectionTitle>
          <p className="text-sm text-slate-400 mb-4">{t("apiDocs.authDesc")}</p>
          <CodeBlock code="Authorization: Bearer epv_1234567890abcdef..." />
          <p className="text-sm text-slate-400 mt-3">
            {t("apiDocs.authTokensLabel")}{" "}
            <a
              href="https://dashboard.mayorsoftware.ch"
              className="text-cyan-400 hover:underline"
            >
              dashboard.mayorsoftware.ch
            </a>{" "}
            {t("apiDocs.authTokensSuffix")}
          </p>
        </section>

        {/* ── Base URLs ── */}
        <section id="base-urls" className="mt-10">
          <SectionTitle id="base-urls">
            {t("apiDocs.baseUrlsTitle")}
          </SectionTitle>
          <div className="space-y-3">
            {[
              {
                label: t("apiDocs.baseUrlRestLabel"),
                value: "https://api.mayorsoftware.ch/v1",
              },
              {
                label: t("apiDocs.baseUrlWsLabel"),
                value: "wss://api.mayorsoftware.ch/v1/asr",
              },
            ].map((u) => (
              <div
                key={u.value}
                className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-slate-800/40 border border-slate-700/50 rounded-lg"
              >
                <span className="text-sm text-slate-400 sm:w-48 shrink-0">
                  {u.label}
                </span>
                <IC>{u.value}</IC>
              </div>
            ))}
          </div>
        </section>

        {/* ── Pricing ── */}
        <section id="pricing" className="mt-10">
          <SectionTitle id="pricing">{t("apiDocs.pricingTitle")}</SectionTitle>
          <p className="text-sm text-slate-400 mb-6">
            {t("apiDocs.pricingDescPre")}{" "}
            <span className="text-slate-200 font-semibold">
              {t("apiDocs.pricingBaseRate")}
            </span>
            . {t("apiDocs.pricingDescPost")}
          </p>
          <div className="space-y-6">
            <div>
              <SubTitle>{t("apiDocs.pricingFileTitle")}</SubTitle>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">
                        {t("apiDocs.pricingColMode")}
                      </th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">
                        {t("apiDocs.pricingColTokensMin")}
                      </th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">
                        {t("apiDocs.pricingColRate")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-slate-800/60">
                      <td className="py-2 px-3 text-slate-300 text-xs">
                        {t("apiDocs.pricingModeNoDia")}
                      </td>
                      <td className="py-2 px-3 text-slate-300 text-xs">4.5</td>
                      <td className="py-2 px-3 text-slate-100 text-xs font-semibold">
                        0.09 CHF/min
                      </td>
                    </tr>
                    <tr className="border-b border-slate-800/60">
                      <td className="py-2 px-3 text-slate-300 text-xs">
                        {t("apiDocs.pricingModeDia")}
                      </td>
                      <td className="py-2 px-3 text-slate-300 text-xs">6.0</td>
                      <td className="py-2 px-3 text-slate-100 text-xs font-semibold">
                        0.12 CHF/min
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <SubTitle>{t("apiDocs.pricingStreamingTitle")}</SubTitle>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">
                        {t("apiDocs.pricingColMode")}
                      </th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">
                        {t("apiDocs.pricingColTokensMin")}
                      </th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">
                        {t("apiDocs.pricingColRate")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-slate-800/60">
                      <td className="py-2 px-3 text-slate-300 text-xs">
                        {t("apiDocs.pricingModeNoDia")}
                      </td>
                      <td className="py-2 px-3 text-slate-300 text-xs">7.0</td>
                      <td className="py-2 px-3 text-slate-100 text-xs font-semibold">
                        0.14 CHF/min
                      </td>
                    </tr>
                    <tr className="border-b border-slate-800/60">
                      <td className="py-2 px-3 text-slate-300 text-xs">
                        {t("apiDocs.pricingModeDia")}
                      </td>
                      <td className="py-2 px-3 text-slate-300 text-xs">8.5</td>
                      <td className="py-2 px-3 text-slate-100 text-xs font-semibold">
                        0.17 CHF/min
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <SubTitle>{t("apiDocs.pricingLlmTitle")}</SubTitle>
              <p className="text-sm text-slate-400">
                {t("apiDocs.pricingLlmDescPre")}{" "}
                <span className="text-slate-200 font-semibold">
                  {t("apiDocs.pricingLlmRate")}
                </span>{" "}
                {t("apiDocs.pricingLlmDescPost")}
              </p>
            </div>
          </div>
        </section>

        {/* ── WebSocket ── */}
        <section id="websocket" className="mt-10">
          <SectionTitle id="websocket">{t("apiDocs.wsTitle")}</SectionTitle>
          <EndpointCard
            method="WS"
            url="wss://api.mayorsoftware.ch/v1/asr"
            title={t("apiDocs.wsEndpointTitle")}
            description={t("apiDocs.wsEndpointDesc")}
          >
            <div>
              <SubTitle>{t("apiDocs.wsConnParams")}</SubTitle>
              <ParamTable
                params={[
                  {
                    name: "token",
                    type: "string",
                    required: true,
                    desc: t("apiDocs.wsParamToken"),
                  },
                  {
                    name: "model / model_id",
                    type: "string",
                    required: false,
                    desc: t("apiDocs.wsParamModel"),
                  },
                  {
                    name: "enable_diarization",
                    type: "boolean",
                    required: false,
                    desc: t("apiDocs.wsParamDiarization"),
                  },
                ]}
              />
            </div>

            <div>
              <SubTitle>{t("apiDocs.wsRateLimits")}</SubTitle>
              <ul className="list-disc list-inside text-sm text-slate-400 space-y-1 ml-2">
                <li>
                  <span className="text-slate-200 font-semibold">
                    {t("apiDocs.wsRateSimultaneous")}
                  </span>{" "}
                  {t("apiDocs.wsRateSimultaneousSuffix")}
                </li>
                <li>
                  <span className="text-slate-200 font-semibold">
                    {t("apiDocs.wsRateNew")}
                  </span>{" "}
                  {t("apiDocs.wsRateNewSuffix")}
                </li>
                <li>{t("apiDocs.wsRateExceeded")}</li>
              </ul>
            </div>

            <div>
              <SubTitle>{t("apiDocs.wsConnectionSequence")}</SubTitle>
              <ol className="list-decimal list-inside text-sm text-slate-400 space-y-3 ml-2">
                <li>
                  <span className="text-slate-200 font-semibold">
                    {t("apiDocs.wsStep1")}
                  </span>
                  <div className="mt-1">
                    <CodeBlock code="wss://api.mayorsoftware.ch/v1/asr?token=epv_xxx&enable_diarization=true" />
                  </div>
                </li>
                <li>
                  <span className="text-slate-200 font-semibold">
                    {t("apiDocs.wsStep2")}
                  </span>
                  <div className="mt-1">
                    <CodeBlock
                      code={'{ "type": "config", "useAudioWorklet": false }'}
                    />
                  </div>
                </li>
                <li>
                  <span className="text-slate-200 font-semibold">
                    {t("apiDocs.wsStep3")}
                  </span>{" "}
                  <span className="text-red-400 text-xs font-bold">
                    {t("apiDocs.wsStep3Required")}
                  </span>
                  <div className="mt-1">
                    <CodeBlock
                      code={
                        '{\n  "type": "config",\n  "voice_signatures": [\n    { "name": "Alice", "embedding": [0.123, -0.456, ...]  },\n    { "name": "Bob",   "embedding": [0.321,  0.654, ...] }\n  ]\n}'
                      }
                    />
                  </div>
                </li>
                <li>
                  <span className="text-slate-200 font-semibold">
                    {t("apiDocs.wsStep4")}
                  </span>
                  <div className="mt-1">
                    <CodeBlock
                      code={
                        '{ "type": "config_ack", "signatures_received": 2 }'
                      }
                    />
                  </div>
                </li>
                <li>
                  <span className="text-slate-200 font-semibold">
                    {t("apiDocs.wsStep5")}
                  </span>
                </li>
              </ol>
            </div>

            <div>
              <SubTitle>{t("apiDocs.wsMessageTypes")}</SubTitle>
              <div className="space-y-4">
                <div>
                  <div className="text-sm font-semibold text-slate-300 mb-2">
                    {t("apiDocs.wsTranscriptionResult")}
                  </div>
                  <CodeBlock
                    code={
                      '{\n  "status": "active_transcription",\n  "lines": [\n    { "speaker": 0, "text": "Transcribed text", "start": "00:00:00.000", "end": "00:00:03.200" }\n  ],\n  "token_usage": {\n    "tokens_consumed": 3.5,\n    "duration_minutes": 0.5\n  }\n}'
                    }
                  />
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-300 mb-2">
                    {t("apiDocs.wsEndSession")}
                  </div>
                  <CodeBlock code={'{ "type": "end_session" }'} />
                  <p className="text-xs text-slate-400 mt-2">
                    {t("apiDocs.wsEndSessionNote")}
                  </p>
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-300 mb-2">
                    {t("apiDocs.wsReadyToStop")}
                  </div>
                  <CodeBlock code={'{ "type": "ready_to_stop" }'} />
                  <p className="text-xs text-slate-400 mt-2">
                    {t("apiDocs.wsReadyToStopNote")}
                  </p>
                </div>
              </div>
            </div>

            <div>
              <SubTitle>{t("apiDocs.wsCodeExamples")}</SubTitle>
              <CodeTabs
                tabs={[
                  { label: "JavaScript", code: WS_JS },
                  { label: "Python", code: WS_PYTHON },
                ]}
              />
            </div>
          </EndpointCard>
        </section>

        {/* ── Models ── */}
        <section id="models" className="mt-10">
          <SectionTitle id="models">{t("apiDocs.modelsTitle")}</SectionTitle>
          <EndpointCard
            method="GET"
            url="https://api.mayorsoftware.ch/v1/models"
            title={t("apiDocs.modelsEndpointTitle")}
            description={t("apiDocs.modelsEndpointDesc")}
          >
            <div>
              <SubTitle>{t("apiDocs.modelsResponse")}</SubTitle>
              <CodeBlock
                code={
                  '{\n  "models": ["standard", "baseline"],\n  "default": "standard",\n  "details": [\n    {\n      "id": "standard",\n      "label": "Whisper Large V3 Turbo",\n      "description": "Primary production model",\n      "backend": "faster-whisper",\n      "model_size": "large-v3-turbo",\n      "language": "fr",\n      "available": true,\n      "notes": "Optimised for livestreams"\n    }\n  ]\n}'
                }
              />
            </div>
            <div>
              <SubTitle>{t("apiDocs.modelsDetailFields")}</SubTitle>
              <FieldTable
                fields={[
                  {
                    name: "id",
                    type: "string",
                    desc: t("apiDocs.fieldModelId"),
                  },
                  {
                    name: "backend",
                    type: "string",
                    desc: t("apiDocs.fieldBackend"),
                  },
                  {
                    name: "model_size",
                    type: "string",
                    desc: t("apiDocs.fieldModelSize"),
                  },
                  {
                    name: "available",
                    type: "boolean",
                    desc: t("apiDocs.fieldAvailable"),
                  },
                  {
                    name: "notes",
                    type: "string",
                    desc: t("apiDocs.fieldNotes"),
                  },
                ]}
              />
            </div>
          </EndpointCard>
        </section>

        {/* ── Voice Signatures ── */}
        <section id="signatures" className="mt-10">
          <SectionTitle id="signatures">
            {t("apiDocs.signaturesTitle")}
          </SectionTitle>
          <EndpointCard
            method="POST"
            url="https://api.mayorsoftware.ch/v1/generate_signatures"
            title={t("apiDocs.signaturesEndpointTitle")}
            description={t("apiDocs.signaturesEndpointDesc")}
          >
            <div>
              <SubTitle>{t("apiDocs.signaturesRequestParams")}</SubTitle>
              <ParamTable
                params={[
                  {
                    name: "audio_files",
                    type: "file[]",
                    required: true,
                    desc: t("apiDocs.sigParamAudioFiles"),
                  },
                  {
                    name: "speaker_names",
                    type: "string",
                    required: true,
                    desc: t("apiDocs.sigParamSpeakerNames"),
                  },
                ]}
              />
            </div>
            <div>
              <SubTitle>{t("apiDocs.signaturesResponse")}</SubTitle>
              <CodeBlock
                code={
                  '{\n  "success": true,\n  "signatures": [\n    { "name": "John Doe", "embedding": [0.123, -0.456, 0.789, ...], "encoder_type": "ecapa-tdnn" },\n    { "name": "Jane Smith", "embedding": [0.321,  0.654, -0.987, ...], "encoder_type": "ecapa-tdnn" }\n  ],\n  "count": 2,\n  "encoder_type": "ecapa-tdnn"\n}'
                }
              />
            </div>
            <div>
              <SubTitle>{t("apiDocs.signaturesCodeExamples")}</SubTitle>
              <CodeTabs
                tabs={[
                  { label: "JavaScript", code: GEN_SIG_JS },
                  { label: "Python", code: GEN_SIG_PYTHON },
                ]}
              />
            </div>
          </EndpointCard>
        </section>

        {/* ── Signature Refinement ── */}
        <section id="refinement" className="mt-10">
          <SectionTitle id="refinement">
            {t("apiDocs.refinementTitle")}
          </SectionTitle>
          <InfoBox color="blue" title={t("apiDocs.refinementInfoTitle")}>
            {t("apiDocs.refinementInfoBody")}
          </InfoBox>

          <div className="mt-6 space-y-4">
            {[
              {
                mode: '"off"',
                badge: t("apiDocs.refinementOffBadge"),
                desc: t("apiDocs.refinementModeOff"),
              },
              {
                mode: '"on"',
                badge: t("apiDocs.refinementOnBadge"),
                desc: t("apiDocs.refinementModeOn"),
              },
              {
                mode: '"compare"',
                badge: t("apiDocs.refinementCompareBadge"),
                desc: t("apiDocs.refinementModeCompare"),
              },
            ].map(({ mode, badge, desc }) => (
              <div
                key={mode}
                className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4"
              >
                <div className="flex items-center gap-2 mb-2">
                  <IC>{mode}</IC>
                  <span className="text-xs text-slate-500">({badge})</span>
                </div>
                <p className="text-sm text-slate-400">{desc}</p>
              </div>
            ))}
          </div>

          <InfoBox color="amber" title={t("apiDocs.refinementPerfTitle")}>
            {t("apiDocs.refinementPerfBody")}
          </InfoBox>

          <div className="mt-6">
            <SubTitle>{t("apiDocs.refinementResponseFields")}</SubTitle>
            <FieldTable
              fields={[
                {
                  name: "mode",
                  type: "string",
                  desc: t("apiDocs.fieldRefMode"),
                },
                {
                  name: "applied",
                  type: "boolean",
                  desc: t("apiDocs.fieldRefApplied"),
                },
                {
                  name: "iterations",
                  type: "integer",
                  desc: t("apiDocs.fieldRefIterations"),
                },
                {
                  name: "stats[].speaker_name",
                  type: "string",
                  desc: t("apiDocs.fieldRefSpeakerName"),
                },
                {
                  name: "stats[].num_frames",
                  type: "integer",
                  desc: t("apiDocs.fieldRefNumFrames"),
                },
                {
                  name: "stats[].total_duration",
                  type: "float",
                  desc: t("apiDocs.fieldRefTotalDuration"),
                },
                {
                  name: "stats[].avg_similarity",
                  type: "float",
                  desc: t("apiDocs.fieldRefAvgSimilarity"),
                },
                {
                  name: "stats[].source",
                  type: "string",
                  desc: t("apiDocs.fieldRefSource"),
                },
              ]}
            />
          </div>
        </section>

        {/* ── Sync Transcription ── */}
        <section id="transcribe-sync" className="mt-10">
          <SectionTitle id="transcribe-sync">
            {t("apiDocs.syncTitle")}
          </SectionTitle>
          <EndpointCard
            method="POST"
            url="https://api.mayorsoftware.ch/v1/transcribe/sync"
            title={t("apiDocs.syncEndpointTitle")}
            description={t("apiDocs.syncEndpointDesc")}
          >
            <div>
              <SubTitle>{t("apiDocs.syncRequestParams")}</SubTitle>
              <ParamTable
                params={[
                  {
                    name: "audio",
                    type: "file",
                    required: true,
                    desc: t("apiDocs.paramAudio"),
                  },
                  {
                    name: "diarization",
                    type: "string",
                    required: false,
                    desc: t("apiDocs.paramDiarization"),
                  },
                  {
                    name: "use_voice_signatures",
                    type: "string",
                    required: false,
                    desc: t("apiDocs.paramUseVoiceSig"),
                  },
                  {
                    name: "voice_signatures",
                    type: "string",
                    required: false,
                    desc: t("apiDocs.paramVoiceSig"),
                  },
                  {
                    name: "voice_similarity_threshold",
                    type: "string",
                    required: false,
                    desc: t("apiDocs.paramSimilarityThreshold"),
                  },
                  {
                    name: "anonymous_similarity_threshold",
                    type: "string",
                    required: false,
                    desc: t("apiDocs.paramAnonymousSimilarityThreshold"),
                  },
                  {
                    name: "model_id",
                    type: "string",
                    required: false,
                    desc: t("apiDocs.paramModelIdOverride"),
                  },
                  {
                    name: "signature_refinement_mode",
                    type: "string",
                    required: false,
                    desc: t("apiDocs.paramRefinementMode"),
                  },
                ]}
              />
            </div>
            <div>
              <SubTitle>{t("apiDocs.syncResponse")}</SubTitle>
              <CodeBlock
                code={
                  '{\n  "text": "Full transcription text",\n  "segments": [\n    { "text": "First segment", "speaker": 0, "start": "00:00:00.000", "end": "00:00:03.200" }\n  ],\n  "filename": "audio.wav",\n  "duration": 15.3,\n  "language": "fr",\n  "diarization_enabled": true,\n  "voice_signatures_used": 0,\n  "diarization_summary": {\n    "total_segments": 5,\n    "unique_speakers": 2,\n    "speaker_durations": { "0": 8.5, "1": 6.8 }\n  },\n  "processing_mode": "sync",\n  "token_usage": {\n    "tokens_consumed": 1.53,\n    "duration_minutes": 0.255\n  }\n}'
                }
              />
            </div>
            <div>
              <SubTitle>{t("apiDocs.syncCodeExamples")}</SubTitle>
              <CodeTabs
                tabs={[
                  { label: "JavaScript", code: SYNC_JS },
                  { label: "Python", code: SYNC_PYTHON },
                ]}
              />
            </div>
          </EndpointCard>
        </section>

        {/* ── Async Submit ── */}
        <section id="transcribe-submit" className="mt-10">
          <SectionTitle id="transcribe-submit">
            {t("apiDocs.submitTitle")}
          </SectionTitle>
          <EndpointCard
            method="POST"
            url="https://api.mayorsoftware.ch/v1/transcribe/submit"
            title={t("apiDocs.submitEndpointTitle")}
            description={t("apiDocs.submitEndpointDesc")}
          >
            <div>
              <SubTitle>{t("apiDocs.submitRequestParams")}</SubTitle>
              <ParamTable
                params={[
                  {
                    name: "audio",
                    type: "file",
                    required: true,
                    desc: t("apiDocs.paramAudio"),
                  },
                  {
                    name: "diarization",
                    type: "string",
                    required: false,
                    desc: t("apiDocs.paramDiarizationSubmit"),
                  },
                  {
                    name: "voice_signatures",
                    type: "string",
                    required: false,
                    desc: t("apiDocs.paramVoiceSig"),
                  },
                  {
                    name: "use_voice_signatures",
                    type: "string",
                    required: false,
                    desc: t("apiDocs.paramUseVoiceSigSubmit"),
                  },
                  {
                    name: "voice_similarity_threshold",
                    type: "string",
                    required: false,
                    desc: t("apiDocs.paramSimilarityThresholdSubmit"),
                  },
                  {
                    name: "anonymous_similarity_threshold",
                    type: "string",
                    required: false,
                    desc: t("apiDocs.paramAnonymousSimilarityThresholdSubmit"),
                  },
                  {
                    name: "model_id",
                    type: "string",
                    required: false,
                    desc: t("apiDocs.paramModelIdSubmit"),
                  },
                  {
                    name: "signature_refinement_mode",
                    type: "string",
                    required: false,
                    desc: t("apiDocs.paramRefinementModeSubmit"),
                  },
                ]}
              />
            </div>
            <div>
              <SubTitle>{t("apiDocs.submitResponse")}</SubTitle>
              <CodeBlock
                code={
                  '{\n  "task_id": "550e8400-e29b-41d4-a716-446655440000",\n  "status": "queued",\n  "message": "Task submitted successfully. Use /transcribe/status/{task_id} to check progress.",\n  "model_id": "standard",\n  "token_usage": {\n    "tokens_consumed": 1.53,\n    "duration_minutes": 0.255,\n    "billing_metadata": { "estimated": true }\n  }\n}'
                }
              />
            </div>
            <div>
              <SubTitle>{t("apiDocs.submitCodeExamples")}</SubTitle>
              <CodeTabs
                tabs={[
                  { label: t("apiDocs.submitJsTab"), code: SUBMIT_JS },
                  { label: t("apiDocs.submitPyTab"), code: SUBMIT_PYTHON },
                ]}
              />
            </div>
          </EndpointCard>
        </section>

        {/* ── Async Status ── */}
        <section id="transcribe-status" className="mt-10">
          <SectionTitle id="transcribe-status">
            {t("apiDocs.statusTitle")}
          </SectionTitle>
          <EndpointCard
            method="GET"
            url="https://api.mayorsoftware.ch/v1/transcribe/status/{task_id}"
            title={t("apiDocs.statusEndpointTitle")}
            description={t("apiDocs.statusEndpointDesc")}
          >
            <div>
              <SubTitle>{t("apiDocs.statusResponse")}</SubTitle>
              <CodeBlock
                code={
                  '{\n  "task_id": "550e8400-e29b-41d4-a716-446655440000",\n  "status": "completed",\n  "progress": 100,\n  "current_step": "Completed successfully",\n  "created_at": "2025-11-07T10:30:00Z",\n  "started_at": "2025-11-07T10:30:01Z",\n  "completed_at": "2025-11-07T10:31:45Z",\n  "filename": "audio.wav",\n  "duration": 104.5,\n  "error": null\n}'
                }
              />
            </div>
            <div>
              <SubTitle>{t("apiDocs.statusValuesTitle")}</SubTitle>
              <FieldTable
                fields={[
                  {
                    name: "queued",
                    type: "—",
                    desc: t("apiDocs.statusQueued"),
                  },
                  {
                    name: "processing",
                    type: "—",
                    desc: t("apiDocs.statusProcessing"),
                  },
                  {
                    name: "completed",
                    type: "—",
                    desc: t("apiDocs.statusCompleted"),
                  },
                  {
                    name: "failed",
                    type: "—",
                    desc: t("apiDocs.statusFailed"),
                  },
                  {
                    name: "cancelled",
                    type: "—",
                    desc: t("apiDocs.statusCancelled"),
                  },
                ]}
              />
            </div>
          </EndpointCard>
        </section>

        {/* ── Async Result ── */}
        <section id="transcribe-result" className="mt-10">
          <SectionTitle id="transcribe-result">
            {t("apiDocs.resultTitle")}
          </SectionTitle>
          <EndpointCard
            method="GET"
            url="https://api.mayorsoftware.ch/v1/transcribe/result/{task_id}"
            title={t("apiDocs.resultEndpointTitle")}
            description={t("apiDocs.resultEndpointDesc")}
          >
            <div>
              <SubTitle>{t("apiDocs.resultResponse")}</SubTitle>
              <CodeBlock
                code={
                  '{\n  "text": "Complete transcription text",\n  "segments": [\n    { "text": "Segment text", "speaker": 0, "start": "00:00:00.000", "end": "00:00:03.200" }\n  ],\n  "filename": "audio.wav",\n  "duration": 120.5,\n  "language": "fr",\n  "diarization_enabled": true,\n  "voice_signatures_used": 2,\n  "model_id": "whisper-large-v3",\n  "diarization_summary": {\n    "total_segments": 25,\n    "unique_speakers": 3,\n    "speaker_durations": { "0": 45.2, "1": 38.7, "2": 36.6 }\n  },\n  "processing_mode": "background",\n  "token_usage": { "tokens_consumed": 12.05, "duration_minutes": 2.008 }\n}'
                }
              />
            </div>
          </EndpointCard>
        </section>

        {/* ── Async Cancel ── */}
        <section id="transcribe-cancel" className="mt-10">
          <SectionTitle id="transcribe-cancel">
            {t("apiDocs.cancelTitle")}
          </SectionTitle>
          <EndpointCard
            method="DELETE"
            url="https://api.mayorsoftware.ch/v1/transcribe/cancel/{task_id}"
            title={t("apiDocs.cancelEndpointTitle")}
            description={t("apiDocs.cancelEndpointDesc")}
          >
            <div>
              <SubTitle>{t("apiDocs.cancelResponse")}</SubTitle>
              <CodeBlock
                code={
                  '{\n  "message": "Task cancelled successfully",\n  "task_id": "550e8400-e29b-41d4-a716-446655440000"\n}'
                }
              />
            </div>
          </EndpointCard>
        </section>

        {/* ── Reference Transcripts ── */}
        <section id="reference-transcript" className="mt-10">
          <SectionTitle id="reference-transcript">
            {t("apiDocs.refTranscriptTitle")}
          </SectionTitle>
          <EndpointCard
            method="GET"
            url="https://api.mayorsoftware.ch/reference-transcript/{filename}"
            title={t("apiDocs.refTranscriptEndpointTitle")}
            description={t("apiDocs.refTranscriptEndpointDesc")}
          >
            <div>
              <SubTitle>{t("apiDocs.refTranscriptPathParams")}</SubTitle>
              <ParamTable
                params={[
                  {
                    name: "filename",
                    type: "string",
                    required: true,
                    desc: t("apiDocs.paramFilename"),
                  },
                ]}
              />
            </div>
            <div>
              <SubTitle>{t("apiDocs.refTranscriptResponse")}</SubTitle>
              <CodeBlock
                code={
                  '{\n  "filename": "session_01.wav",\n  "reference_text": "Mesdames et Messieurs, bienvenue...",\n  "duration": 123.4,\n  "source": "Swiss Parliament"\n}'
                }
              />
              <p className="text-xs text-slate-400 mt-2">
                {t("apiDocs.refTranscriptNote")}
              </p>
            </div>
          </EndpointCard>
        </section>

        {/* ── Stats ── */}
        <section id="stats" className="mt-10">
          <SectionTitle id="stats">{t("apiDocs.statsTitle")}</SectionTitle>
          <EndpointCard
            method="GET"
            url="/stats"
            title={t("apiDocs.statsEndpointTitle")}
            description={t("apiDocs.statsEndpointDesc")}
          >
            <div>
              <SubTitle>{t("apiDocs.statsResponse")}</SubTitle>
              <CodeBlock
                code={
                  '{\n  "active_connections_per_token": {\n    "abc123...": 2,\n    "def456...": 1\n  },\n  "total_active_connections": 3,\n  "rate_limiting": {\n    "max_concurrent_per_token": 3,\n    "max_per_minute_per_token": 10\n  }\n}'
                }
              />
            </div>
            <div>
              <SubTitle>{t("apiDocs.statsFields")}</SubTitle>
              <FieldTable
                fields={[
                  {
                    name: "active_connections_per_token",
                    type: "object",
                    desc: t("apiDocs.fieldActiveConnPerToken"),
                  },
                  {
                    name: "total_active_connections",
                    type: "integer",
                    desc: t("apiDocs.fieldTotalActiveConn"),
                  },
                  {
                    name: "rate_limiting",
                    type: "object",
                    desc: t("apiDocs.fieldRateLimits"),
                  },
                ]}
              />
            </div>
          </EndpointCard>
        </section>

        {/* ── Health ── */}
        <section id="health" className="mt-10">
          <SectionTitle id="health">{t("apiDocs.healthTitle")}</SectionTitle>
          <EndpointCard
            method="GET"
            url="https://api.mayorsoftware.ch/health"
            title={t("apiDocs.healthEndpointTitle")}
            description={t("apiDocs.healthEndpointDesc")}
          >
            <div>
              <SubTitle>{t("apiDocs.healthResponse")}</SubTitle>
              <CodeBlock
                code={
                  '{\n  "status": "healthy",\n  "active_connections": 2,\n  "unique_tokens": 2\n}'
                }
              />
            </div>
          </EndpointCard>
        </section>

        {/* ── LLM Chat ── */}
        <section id="llm-chat" className="mt-10 mb-16">
          <SectionTitle id="llm-chat">{t("apiDocs.llmTitle")}</SectionTitle>
          <EndpointCard
            method="POST"
            url="https://api.mayorsoftware.ch/v1/llm/chat/completions"
            title={t("apiDocs.llmEndpointTitle")}
            description={t("apiDocs.llmEndpointDesc")}
          >
            <div>
              <SubTitle>{t("apiDocs.llmRequestBody")}</SubTitle>
              <ParamTable
                params={[
                  {
                    name: "messages",
                    type: "array",
                    required: true,
                    desc: t("apiDocs.llmParamMessages"),
                  },
                  {
                    name: "model",
                    type: "string",
                    required: false,
                    desc: t("apiDocs.llmParamModel"),
                  },
                  {
                    name: "profile_type",
                    type: "string",
                    required: false,
                    desc: t("apiDocs.llmParamProfileType"),
                  },
                  {
                    name: "temperature",
                    type: "float",
                    required: false,
                    desc: t("apiDocs.llmParamTemperature"),
                  },
                  {
                    name: "max_tokens",
                    type: "integer",
                    required: false,
                    desc: t("apiDocs.llmParamMaxTokens"),
                  },
                  {
                    name: "stream",
                    type: "boolean",
                    required: false,
                    desc: t("apiDocs.llmParamStream"),
                  },
                ]}
              />
            </div>
            <div>
              <SubTitle>{t("apiDocs.llmRequest")}</SubTitle>
              <CodeBlock
                code={
                  '{\n  "messages": [\n    { "role": "system", "content": "You are a helpful assistant." },\n    { "role": "user",   "content": "Summarize this meeting transcript..." }\n  ],\n  "model": "Qwen/Qwen3.8-27B-FP8",\n  "profile_type": "standard",\n  "temperature": 1.0,\n  "max_tokens": 2048,\n  "stream": false\n}'
                }
              />
            </div>
            <div>
              <SubTitle>{t("apiDocs.llmResponse")}</SubTitle>
              <CodeBlock
                code={
                  '{\n  "id": "chatcmpl-7890123456",\n  "model": "Qwen/Qwen3.8-27B-FP8",\n  "choices": [\n    {\n      "message": { "role": "assistant", "content": "Generated response text..." },\n      "finish_reason": "stop"\n    }\n  ],\n  "usage": {\n    "prompt_tokens": 45,\n    "completion_tokens": 123,\n    "total_tokens": 168\n  }\n}'
                }
              />
            </div>
            <div>
              <SubTitle>{t("apiDocs.llmCodeExample")}</SubTitle>
              <CodeBlock code={LLM_JS} />
            </div>
          </EndpointCard>
        </section>
      </div>
    </div>
  );
};

export default ApiDocumentation;
